import parseISO from 'date-fns/parseISO';
import * as React from 'react';
import * as O from 'fp-ts/Option';
import * as A from 'fp-ts/Array';
import { pipe } from 'fp-ts/lib/function';
import { Redirect } from 'react-router';
import isPast from 'date-fns/isPast';
import { formatInTimeZone } from 'date-fns-tz';
import { BackLink } from '../../components/BackLink';
import {
  FullMatchFragment,
  useDeleteMatchMutation,
} from '../../generated/graphql';
import { paths } from '../../paths';
import { dateFormats } from '../../utils/dateFormats';
import { useAlert } from '../../contexts/AlertContext';

import { useAnalytics } from '../../contexts/AnalyticsContext';
import { LinkListItem } from '../../components/LinkListItem';
import { useConfirm } from '../../contexts/ConfirmContext';
import { sequenceTOption } from '../../function';
import { Title } from '../../components/Title';
import { useTitle } from '../../useTitle';
import { MatchTeams } from './components/MatchTeams';
import { getScoreInfo } from './utils';

interface Props {
  match: FullMatchFragment;
}

const confirmProps = {
  title: 'Delete match',
  okButtonText: 'Yes, delete',
  destructive: true,
  children: (
    <>
      <p>
        Are you sure you want to delete this match? All related stats and votes
        will be deleted.
      </p>
      <p>This cannot be undone!</p>
    </>
  ),
};

export const MatchSettings: React.FC<Props> = ({ match }) => {
  const playedAt = parseISO(match.playedAt);
  const confirm = useConfirm();
  const alert = useAlert();
  useTitle('Match Settings');
  const analytics = useAnalytics();
  const [deleteMatchResponse, deleteMatch] = useDeleteMatchMutation();

  const onDeleteMatch = async () => {
    analytics.track('Form Submitted', { form: 'delete_match' });
    const response = await confirm(confirmProps);
    if (response) {
      deleteMatch({ id: match.id }).then((res) => {
        if (res.data?.deleteMatch?.entity) {
          alert('Match deleted 🗑');
          analytics.track('Data Submit Succeeded', { form: 'delete_match' });
          return;
        }
        if (res.error) {
          alert(res.error.message, 'danger');
          analytics.track('Data Submit Failed', {
            form: 'delete_match',
            error: res.error.message,
          });
          return;
        }
      });
    }
  };

  if (deleteMatchResponse.data?.deleteMatch?.entity) {
    return <Redirect to={paths.home()} />;
  }

  return (
    <div className="space-y-6">
      <BackLink to={paths.match.home(match.id)}>Back to match</BackLink>
      <Title>Match Settings</Title>
      <div className="space-y-2">
        <h3>
          <MatchTeams match={match} />
        </h3>
        <time dateTime={match.playedAt} className="text-xs block text-grey-600">
          {formatInTimeZone(playedAt, match.timeZone, dateFormats.longDate)}
        </time>
      </div>
      <div className="grid grid-cols-1 divide-y divide-grey-400">
        <div className="grid grid-cols-1 divide-y divide-grey-100 py-4">
          <LinkListItem
            to={paths.match.settings.details(match.id)}
            description={`KO ${formatInTimeZone(
              playedAt,
              match.timeZone,
              dateFormats.time,
            )}${pipe(
              match.meetAt,
              O.fromNullable,
              O.map(parseISO),
              O.map((meet) =>
                formatInTimeZone(meet, match.timeZone, dateFormats.time),
              ),
              O.map((t) => ` | Meet ${t}`),
              O.getOrElse(() => ''),
            )}`}
          >
            Edit match details
          </LinkListItem>

          <LinkListItem
            to={paths.match.settings.location(match.id)}
            description={match.location?.address || undefined}
          >
            Set location
          </LinkListItem>

          <LinkListItem
            to={paths.match.settings.squad(match.id)}
            description={
              A.isNonEmpty(match.players)
                ? `${match.players.length} players selected`
                : undefined
            }
          >
            Set squad
          </LinkListItem>

          <LinkListItem
            to={paths.match.settings.kit(match.id)}
            description={
              A.isNonEmpty(match.kitItems)
                ? match.kitItems.map((k) => k.name).join(', ')
                : undefined
            }
          >
            Set match kit
          </LinkListItem>
        </div>

        {isPast(playedAt) ? (
          <div className="grid grid-cols-1 divide-y divide-grey-100 py-4">
            <LinkListItem
              to={paths.match.settings.score(match.id)}
              description={pipe(
                getScoreInfo(match),
                ({ homeGoals, awayGoals }) =>
                  sequenceTOption(
                    O.fromNullable(homeGoals),
                    O.fromNullable(awayGoals),
                  ),
                O.map(([homeGoals, awayGoals]) => `${homeGoals}-${awayGoals}`),
                O.toUndefined,
              )}
            >
              Set score
            </LinkListItem>

            <LinkListItem to={paths.match.settings.scorers(match.id)}>
              Set scorers
            </LinkListItem>

            <LinkListItem to={paths.match.settings.assists(match.id)}>
              Set assists
            </LinkListItem>

            <LinkListItem to={paths.match.settings.cards(match.id)}>
              Set cards
            </LinkListItem>
            <LinkListItem to={paths.match.settings.matchReport(match.id)}>
              Set match report
            </LinkListItem>
          </div>
        ) : null}
        <div className="py-4">
          <button
            className="text-red-500 hover:text-red-400"
            onClick={onDeleteMatch}
          >
            Delete match
          </button>
        </div>
      </div>
    </div>
  );
};
